import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Eye, FileText, Plus } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  Input,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  tpsTaxFormsApi,
  type TaxForm,
  type TaxFormCandidate,
  type TaxFormType,
} from "../api/tpsTaxForms";
import {
  BatchConfirmDialog,
  BatchResultBanner,
  BatchRowCheckbox,
  BatchRowError,
  BatchSelectionHeader,
  useBatchApprove,
} from "../components/BatchApprove";

// TPS Servicing Portal PR-I (WS4) — the firm's "Tax forms" MANAGEMENT surface.
// The servicer is the payer of record for the 1099 information returns Phoenix
// helps it administer. Lifecycle: select (from a computed candidate or the form
// library) → prefill → edit (Draft only) → approve (LOCKS) → send (Copy B to the
// recipient's portal) → optional IRS e-file. A correction is a new row that
// supersedes the prior one. No React Query in the portal — load is plain
// useState, and every write re-runs the list loader the caller owns.

const STATUS_TONE: Record<string, StatusTone> = {
  Draft: "neutral",
  Approved: "progress",
  Sent: "ok",
  Superseded: "muted",
};

function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? "neutral";
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** The default tax year to show first — the prior calendar year (1099s are filed
 *  the year after the income year). */
function defaultTaxYear(): number {
  return new Date().getUTCFullYear() - 1;
}

/** A small range of selectable tax years around the default. */
function taxYearOptions(): number[] {
  const base = defaultTaxYear();
  return [base + 1, base, base - 1, base - 2, base - 3];
}

const SELECT_CLASS =
  "h-10 appearance-none rounded-md border border-border-light bg-white-pure px-space-3 text-small text-onyx focus-visible:outline-none focus-visible:shadow-goldRing";

export function TpsTaxFormsTab() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const openForm = params.get("form");

  const yearParam = params.get("taxYear");
  const parsedYear = yearParam ? Number(yearParam) : NaN;
  const taxYear = Number.isFinite(parsedYear) ? parsedYear : defaultTaxYear();

  const [rows, setRows] = useState<TaxForm[] | null>(null);
  const [candidates, setCandidates] = useState<TaxFormCandidate[] | null>(null);
  const [library, setLibrary] = useState<TaxFormType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const loadList = useCallback(async () => {
    const [forms, cands] = await Promise.all([
      tpsTaxFormsApi.list(taxYear),
      tpsTaxFormsApi.candidates(taxYear),
    ]);
    setRows(forms);
    setCandidates(cands);
    return forms;
  }, [taxYear]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setCandidates(null);
    void (async () => {
      try {
        const [forms, cands] = await Promise.all([
          tpsTaxFormsApi.list(taxYear),
          tpsTaxFormsApi.candidates(taxYear),
        ]);
        if (!cancelled) {
          setRows(forms);
          setCandidates(cands);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your tax forms."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taxYear]);

  // SP-2b — checkbox batch approve over the Draft forms. An informational
  // form (e.g. 1099-K) that can't be approved reports its reason per row.
  const batch = useBatchApprove({
    approve: (ids) => tpsTaxFormsApi.batchApprove(ids),
    refresh: loadList,
    successText: (n) => `All ${n} form${n === 1 ? "" : "s"} approved.`,
    onRequestError: (err) =>
      toast.error(errorMessage(err, "Couldn't approve the selected forms. Try again.")),
  });

  // The form library is year-independent; load it once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsTaxFormsApi.library();
        if (!cancelled) setLibrary(data);
      } catch {
        /* the picker degrades gracefully when the library can't load */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenForm = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("form", id);
    else updated.delete("form");
    setParams(updated, { replace: true });
  };

  const setTaxYear = (year: number) => {
    const updated = new URLSearchParams(params);
    updated.set("taxYear", String(year));
    setParams(updated, { replace: true });
    // The selection is per-year — a year switch reloads the rows.
    batch.reset();
  };

  if (openForm) {
    return (
      <FormDetail formId={openForm} onBack={() => setOpenForm(null)} refreshList={loadList} />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  return (
    <section aria-label="Tax forms" className="flex flex-col gap-space-4">
      <div className="flex flex-wrap items-end justify-between gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Tax year</span>
          <select
            value={taxYear}
            onChange={(e) => setTaxYear(Number(e.target.value))}
            aria-label="Tax year"
            data-testid="tps-tax-forms-year"
            className={SELECT_CLASS}
          >
            {taxYearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setCreating(true)}
          disabled={!library}
          data-testid="tps-tax-forms-new"
        >
          <Plus size={16} aria-hidden />
          New form
        </Button>
      </div>

      {creating && library && (
        <NewFormPicker
          library={library}
          taxYear={taxYear}
          onCancel={() => setCreating(false)}
          onCreated={(form) => {
            setCreating(false);
            void loadList();
            setOpenForm(form.id);
          }}
        />
      )}

      {!rows || !candidates ? (
        <SkeletonList rows={4} data-testid="tps-tax-forms-loading" />
      ) : (
        <>
          <CandidatesSection
            candidates={candidates}
            taxYear={taxYear}
            refreshList={loadList}
            onCreated={(form) => setOpenForm(form.id)}
          />

          <DetailSection title={`Forms — ${taxYear}`}>
            {rows.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No forms yet"
                description="Create a form from a candidate above or with the New form picker, then prefill, approve, and send it to the recipient."
              />
            ) : (
              <div className="flex flex-col gap-space-3">
                <BatchResultBanner batch={batch} testIdPrefix="tps-tax-forms-batch" />
                <BatchSelectionHeader
                  batch={batch}
                  selectableIds={rows.filter((f) => f.status === "Draft").map((f) => f.id)}
                  selectAllLabel="Select all awaiting approval"
                  actionLabel={`Approve ${batch.selected.size} form${
                    batch.selected.size === 1 ? "" : "s"
                  }`}
                  testIdPrefix="tps-tax-forms-batch"
                />
                {rows.map((form) => (
                  <div key={form.id} className="flex flex-col gap-space-1">
                    <div className="flex items-start gap-space-2">
                      {form.status === "Draft" && (
                        <BatchRowCheckbox
                          id={form.id}
                          batch={batch}
                          label={`Select form ${form.formTitle} — ${form.recipientName}`}
                          testIdPrefix="tps-tax-forms-batch"
                        />
                      )}
                      <ListCard
                        className="min-w-0 flex-1"
                        data-testid={`tps-tax-form-row-${form.id}`}
                        title={`${form.formTitle} — ${form.recipientName}`}
                        status={
                          <StatusBadge tone={statusTone(form.status)}>{form.status}</StatusBadge>
                        }
                        meta={
                          <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                            <span>{formatCurrency(form.box1Amount)}</span>
                            <span aria-hidden>·</span>
                            <span>{form.box1Label}</span>
                            <span aria-hidden>·</span>
                            <span>
                              TIN{" "}
                              {form.hasTaxId ? `on file (${form.taxIdMasked ?? "•••"})` : "missing"}
                            </span>
                          </span>
                        }
                        actions={
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setOpenForm(form.id)}
                            data-testid={`tps-tax-form-open-${form.id}`}
                          >
                            {form.status === "Draft" ? "Edit" : "View"}
                          </Button>
                        }
                      />
                    </div>
                    <BatchRowError
                      error={batch.rowErrors.get(form.id)}
                      testId={`tps-tax-forms-batch-error-${form.id}`}
                    />
                  </div>
                ))}
                <BatchConfirmDialog
                  batch={batch}
                  title="Approve forms"
                  body={`Approve ${batch.selected.size} form${
                    batch.selected.size === 1 ? "" : "s"
                  }? An approved form is locked for editing.`}
                  confirmLabel={`Approve ${batch.selected.size} form${
                    batch.selected.size === 1 ? "" : "s"
                  }`}
                  testIdPrefix="tps-tax-forms-batch"
                />
              </div>
            )}
          </DetailSection>
        </>
      )}
    </section>
  );
}

function CandidatesSection({
  candidates,
  taxYear,
  refreshList,
  onCreated,
}: {
  candidates: TaxFormCandidate[];
  taxYear: number;
  refreshList: () => Promise<unknown>;
  onCreated: (form: TaxForm) => void;
}) {
  // Only the not-yet-created candidates are actionable; once created they live
  // in the Forms list below.
  const open = candidates.filter((c) => !c.alreadyCreated);
  if (open.length === 0) return null;

  return (
    <DetailSection title="Suggested forms">
      <p className="m-0 mb-space-3 text-small text-gray-mid">
        Recipients we think you owe a 1099 for {taxYear}. Create one to prefill the amounts, then
        review and send.
      </p>
      <div className="flex flex-col gap-space-3">
        {open.map((cand) => (
          <CandidateRow
            key={`${cand.formType}-${cand.recipientId}-${cand.loanId ?? "none"}`}
            candidate={cand}
            taxYear={taxYear}
            refreshList={refreshList}
            onCreated={onCreated}
          />
        ))}
      </div>
    </DetailSection>
  );
}

function CandidateRow({
  candidate,
  taxYear,
  refreshList,
  onCreated,
}: {
  candidate: TaxFormCandidate;
  taxYear: number;
  refreshList: () => Promise<unknown>;
  onCreated: (form: TaxForm) => void;
}) {
  const toast = useToast();
  const create = useSubmittingMutation(
    () =>
      tpsTaxFormsApi.create({
        taxYear,
        formType: candidate.formType,
        recipientType: candidate.recipientType,
        recipientId: candidate.recipientId,
        loanId: candidate.loanId,
      }),
    {
      refresh: refreshList,
      onSuccess: (form) => {
        toast.success("Form created and prefilled.");
        onCreated(form);
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't create the form. Try again.")),
    },
  );

  return (
    <ListCard
      data-testid={`tps-tax-candidate-${candidate.recipientId}`}
      title={`${candidate.formTitle} — ${candidate.recipientName}`}
      meta={
        <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
          <span>Suggested {formatCurrency(candidate.suggestedBox1Amount)}</span>
          <span aria-hidden>·</span>
          <span>TIN {candidate.hasTaxIdOnFile ? "on file" : "missing"}</span>
        </span>
      }
      actions={
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void create.submit()}
          loading={create.pending}
          disabled={create.pending}
          data-testid={`tps-tax-candidate-create-${candidate.recipientId}`}
        >
          Create
        </Button>
      }
    />
  );
}

function NewFormPicker({
  library,
  taxYear,
  onCancel,
  onCreated,
}: {
  library: TaxFormType[];
  taxYear: number;
  onCancel: () => void;
  onCreated: (form: TaxForm) => void;
}) {
  const toast = useToast();
  const [formType, setFormType] = useState("");
  const [recipientType, setRecipientType] = useState("");
  const [recipientId, setRecipientId] = useState("");

  const selected = useMemo(
    () => library.find((f) => f.formType === formType) ?? null,
    [library, formType],
  );

  // When a form type is chosen, default the recipient type to the form's
  // default; the firm can still override it for a manual blank.
  useEffect(() => {
    if (selected) setRecipientType(selected.defaultRecipientType);
  }, [selected]);

  const informational = selected?.isInformational ?? false;
  const recipientIdValid = recipientId.trim().length > 0;

  const create = useSubmittingMutation(
    () =>
      tpsTaxFormsApi.create({
        taxYear,
        formType,
        recipientType: recipientType.trim(),
        recipientId: recipientId.trim(),
      }),
    {
      onSuccess: (form) => {
        toast.success("Form created.");
        onCreated(form);
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't create the form. Try again.")),
    },
  );

  const canCreate =
    !!selected && !informational && recipientType.trim().length > 0 && recipientIdValid;

  return (
    <DetailSection title="New form" data-testid="tps-tax-forms-new-picker">
      <div className="flex flex-col gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Form type</span>
          <select
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            aria-label="Form type"
            data-testid="tps-tax-forms-new-type"
            className={SELECT_CLASS}
          >
            <option value="">Select a form…</option>
            {library.map((f) => (
              <option key={f.formType} value={f.formType}>
                {f.title}
              </option>
            ))}
          </select>
        </label>

        {informational && (
          <div
            role="note"
            data-testid="tps-tax-forms-informational"
            className="flex items-start gap-space-2 rounded-md border border-status-info bg-status-info/10 p-space-3"
          >
            <AlertTriangle size={18} className="mt-[2px] shrink-0 text-status-info" aria-hidden />
            <p className="m-0 text-small text-onyx">
              {selected?.title} is an informational return filed by the payment settlement entity —
              it isn't prepared here.
            </p>
          </div>
        )}

        {selected && !informational && (
          <>
            <label className="flex flex-col gap-space-1">
              <span className="text-caption text-gray-mid uppercase tracking-label">
                Recipient type
              </span>
              <Input
                value={recipientType}
                onChange={(e) => setRecipientType(e.target.value)}
                aria-label="Recipient type"
                data-testid="tps-tax-forms-new-recipient-type"
              />
            </label>
            <label className="flex flex-col gap-space-1">
              <span className="text-caption text-gray-mid uppercase tracking-label">
                Recipient ID
              </span>
              <Input
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                invalid={!recipientIdValid}
                placeholder="The recipient's record id"
                aria-label="Recipient ID"
                data-testid="tps-tax-forms-new-recipient-id"
              />
            </label>
          </>
        )}

        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void create.submit()}
            disabled={!canCreate || create.pending}
            loading={create.pending}
            data-testid="tps-tax-forms-new-create"
          >
            Create form
          </Button>
          <Button type="button" variant="text" size="sm" onClick={onCancel} disabled={create.pending}>
            Cancel
          </Button>
        </div>
      </div>
    </DetailSection>
  );
}

function FormDetail({
  formId,
  onBack,
  refreshList,
}: {
  formId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [form, setForm] = useState<TaxForm | null>(null);
  const [library, setLibrary] = useState<TaxFormType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [revealedTin, setRevealedTin] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    const data = await tpsTaxFormsApi.get(formId);
    setForm(data);
    return data;
  }, [formId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsTaxFormsApi.get(formId);
        if (!cancelled) setForm(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this form."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsTaxFormsApi.library();
        if (!cancelled) setLibrary(data);
      } catch {
        /* the additional-box editor degrades to Box 1 only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the rendered PDF as an object URL for the iframe (revoked on unmount).
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await tpsTaxFormsApi.pdf(formId);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch {
        /* preview is non-fatal — the firm can still act on the form */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [formId]);

  const formType = useMemo(
    () => library?.find((f) => f.formType === form?.formType) ?? null,
    [library, form],
  );

  const approve = useSubmittingMutation(() => tpsTaxFormsApi.approve(formId), {
    refresh: [refreshList, loadDetail],
    onSuccess: () => toast.success("Form approved and locked."),
    onError: (err) => toast.error(errorMessage(err, "Couldn't approve. Try again.")),
  });

  const send = useSubmittingMutation(() => tpsTaxFormsApi.send(formId), {
    refresh: [refreshList, loadDetail],
    onSuccess: () => toast.success("Copy B sent to the recipient."),
    onError: (err) => toast.error(errorMessage(err, "Couldn't send. Try again.")),
  });

  const efile = useSubmittingMutation(() => tpsTaxFormsApi.efile(formId), {
    refresh: [refreshList, loadDetail],
    onSuccess: () => toast.success("IRS e-file authorized."),
    onError: (err) => toast.error(errorMessage(err, "Couldn't authorize e-file. Try again.")),
  });

  const reveal = useSubmittingMutation(() => tpsTaxFormsApi.revealTin(formId), {
    onSuccess: (res) => setRevealedTin(res.taxId ?? "No TIN on file"),
    onError: (err) => toast.error(errorMessage(err, "Couldn't reveal the TIN.")),
  });

  const openPdf = async () => {
    try {
      const blob = await tpsTaxFormsApi.pdf(formId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't open the PDF."));
    }
  };

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="tps-tax-form-back">
      <ArrowLeft size={16} aria-hidden />
      Back to tax forms
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={6} />
        </DetailSection>
      </div>
    );
  }

  const pending = approve.pending || send.pending || efile.pending;
  const isDraft = form.status === "Draft";
  const isApproved = form.status === "Approved";

  if (editing && isDraft) {
    return (
      <FormEditor
        form={form}
        formType={formType}
        onBack={() => setEditing(false)}
        onSaved={(updated) => {
          setForm(updated);
          setEditing(false);
        }}
        refreshList={refreshList}
      />
    );
  }

  return (
    <section
      aria-label={`${form.formTitle} for ${form.recipientName}`}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={statusTone(form.status)}>{form.status}</StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {form.formTitle} — {form.taxYear}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">
          {form.recipientName}
          {form.preparedAt && (
            <>
              {" · Prepared "}
              <Timestamp value={form.preparedAt} />
            </>
          )}
          {form.sentAt && (
            <>
              {" · Sent "}
              <Timestamp value={form.sentAt} />
            </>
          )}
        </p>
      </div>

      <DetailSection title="Recipient">
        <dl className="grid grid-cols-2 gap-x-space-4 gap-y-space-2 text-small">
          <Line label="Name" value={form.recipientName} />
          <Line label="Email" value={form.recipientEmail || "—"} />
          <Line
            label="Tax ID"
            value={
              revealedTin ?? (form.hasTaxId ? form.taxIdMasked ?? "On file" : "Not on file")
            }
          />
          <div className="flex items-end">
            {form.hasTaxId && revealedTin === null && (
              <Button
                type="button"
                variant="text"
                size="sm"
                onClick={() => void reveal.submit()}
                loading={reveal.pending}
                disabled={reveal.pending}
                data-testid="tps-tax-form-reveal-tin"
              >
                <Eye size={16} aria-hidden />
                Reveal TIN
              </Button>
            )}
          </div>
        </dl>
      </DetailSection>

      <DetailSection title="Amounts">
        <dl className="grid grid-cols-2 gap-x-space-4 gap-y-space-2 text-small">
          <Line label="Gross amount" value={formatCurrency(form.grossAmount)} />
          <Line label={form.box1Label} value={formatCurrency(form.box1Amount)} strong />
          {formType?.additionalBoxes.map((box) => {
            const raw = form.formData?.[box.key];
            const value = box.isCurrency
              ? formatCurrency(Number(raw ?? 0))
              : raw && raw.length > 0
                ? raw
                : "—";
            return <Line key={box.key} label={box.label} value={value} />;
          })}
        </dl>
      </DetailSection>

      <DetailSection title="Copy B preview">
        {pdfUrl ? (
          <iframe
            title="Form PDF"
            src={pdfUrl}
            data-testid="tps-tax-form-pdf"
            className="h-[640px] w-full rounded-md border border-border-light bg-white-pure"
          />
        ) : (
          <SkeletonText lines={4} />
        )}
      </DetailSection>

      <DetailSection title="Actions">
        <div className="flex flex-wrap items-center gap-space-2">
          {isDraft && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={pending}
              data-testid="tps-tax-form-edit"
            >
              Edit
            </Button>
          )}
          {isDraft && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void approve.submit()}
              loading={approve.pending}
              disabled={pending}
              data-testid="tps-tax-form-approve"
            >
              Approve &amp; lock
            </Button>
          )}
          {isApproved && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void send.submit()}
              loading={send.pending}
              disabled={pending}
              data-testid="tps-tax-form-send"
            >
              Send Copy B
            </Button>
          )}
          {(isApproved || form.status === "Sent") && form.eFileStatus !== "Authorized" && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void efile.submit()}
              loading={efile.pending}
              disabled={pending}
              data-testid="tps-tax-form-efile"
            >
              Authorize IRS e-file
            </Button>
          )}
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => void openPdf()}
            data-testid="tps-tax-form-print"
          >
            View / print PDF
          </Button>
          {form.eFileStatus === "Authorized" && (
            <span className="text-caption text-gray-mid" data-testid="tps-tax-form-efile-done">
              IRS e-file authorized
              {form.eFileAuthorizedAt && (
                <>
                  {" "}
                  <Timestamp value={form.eFileAuthorizedAt} />
                </>
              )}
            </span>
          )}
        </div>
      </DetailSection>
    </section>
  );
}

function FormEditor({
  form,
  formType,
  onBack,
  onSaved,
  refreshList,
}: {
  form: TaxForm;
  formType: TaxFormType | null;
  onBack: () => void;
  onSaved: (updated: TaxForm) => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [recipientName, setRecipientName] = useState(form.recipientName);
  const [recipientEmail, setRecipientEmail] = useState(form.recipientEmail ?? "");
  const [taxId, setTaxId] = useState("");
  const [box1, setBox1] = useState(String(form.box1Amount));
  // Additional boxes — seeded from the stored formData; the firm edits the
  // raw value the renderer formats.
  const [boxValues, setBoxValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const box of formType?.additionalBoxes ?? []) {
      initial[box.key] = form.formData?.[box.key] ?? "";
    }
    return initial;
  });

  const save = useSubmittingMutation(
    () =>
      tpsTaxFormsApi.update(form.id, {
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
        // "" clears the TIN; leave it untouched (undefined) when the firm
        // didn't type a replacement so the existing TIN is preserved.
        taxId: taxId.length > 0 ? taxId.trim() : undefined,
        box1Amount: Number(box1) || 0,
        formData: Object.keys(boxValues).length > 0 ? boxValues : undefined,
      }),
    {
      refresh: refreshList,
      onSuccess: (updated) => {
        toast.success("Form updated.");
        onSaved(updated);
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't save. Try again.")),
    },
  );

  const backButton = (
    <Button
      type="button"
      variant="text"
      size="sm"
      onClick={onBack}
      data-testid="tps-tax-form-edit-back"
    >
      <ArrowLeft size={16} aria-hidden />
      Back to form
    </Button>
  );

  const box1Invalid = box1.trim().length === 0 || Number.isNaN(Number(box1));

  return (
    <section aria-label={`Edit ${form.formTitle}`} className="flex flex-col gap-space-4">
      {backButton}

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          Edit {form.formTitle} — {form.taxYear}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">{form.recipientName}</p>
      </div>

      <DetailSection title="Recipient">
        <div className="flex flex-col gap-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">Name</span>
            <Input
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              disabled={save.pending}
              aria-label="Recipient name"
              data-testid="tps-tax-form-edit-name"
            />
          </label>
          <label className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">Email</span>
            <Input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              disabled={save.pending}
              aria-label="Recipient email"
              data-testid="tps-tax-form-edit-email"
            />
          </label>
          <label className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">
              Tax ID {form.hasTaxId ? `(currently ${form.taxIdMasked ?? "on file"})` : ""}
            </span>
            <Input
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              disabled={save.pending}
              placeholder={form.hasTaxId ? "Leave blank to keep the current TIN" : "TIN"}
              aria-label="Tax ID"
              data-testid="tps-tax-form-edit-tin"
            />
          </label>
        </div>
      </DetailSection>

      <DetailSection title="Amounts">
        <div className="flex flex-col gap-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">
              {form.box1Label}
            </span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={box1}
              onChange={(e) => setBox1(e.target.value)}
              disabled={save.pending}
              invalid={box1Invalid}
              aria-label={form.box1Label}
              data-testid="tps-tax-form-edit-box1"
            />
          </label>
          {(formType?.additionalBoxes ?? []).map((box) => (
            <label key={box.key} className="flex flex-col gap-space-1">
              <span className="text-caption text-gray-mid uppercase tracking-label">
                {box.label}
              </span>
              <Input
                type={box.isCurrency ? "number" : "text"}
                inputMode={box.isCurrency ? "decimal" : undefined}
                step={box.isCurrency ? "0.01" : undefined}
                value={boxValues[box.key] ?? ""}
                onChange={(e) =>
                  setBoxValues((prev) => ({ ...prev, [box.key]: e.target.value }))
                }
                disabled={save.pending}
                aria-label={box.label}
                data-testid={`tps-tax-form-edit-box-${box.key}`}
              />
            </label>
          ))}
        </div>
      </DetailSection>

      <div className="flex flex-wrap items-center gap-space-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void save.submit()}
          loading={save.pending}
          disabled={save.pending || recipientName.trim().length === 0 || box1Invalid}
          data-testid="tps-tax-form-edit-save"
        >
          Save
        </Button>
        <Button type="button" variant="text" size="sm" onClick={onBack} disabled={save.pending}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <>
      <dt className={strong ? "font-semibold text-onyx" : "text-gray-mid"}>{label}</dt>
      <dd className={`m-0 text-right ${strong ? "font-semibold text-onyx" : "text-onyx"}`}>
        {value}
      </dd>
    </>
  );
}
